import axios from "axios";
import { API_URL } from "@/config";
import { resetStateToDefault } from "@/lib/store/index.js";

const defaultState = Object.freeze({
  readAt: null,
  hasUnreadMessages: false,
  unreadNotifications: {},
  debtCancellations: [],
  debtCancellationsSettled: false,
  notifications: [], // Notification[]
  placementPreview: null,
});

let notificationSequence = 0;

const store = {
  namespaced: true,

  state: {
    ...defaultState,
  },

  getters: {
    readAt: (state) => state.readAt,
    hasUnreadMessages: (state) => state.hasUnreadMessages,
    unreadNotifications: (state) => state.unreadNotifications,
    debtCancellations: (state) => state.debtCancellations,
    hasDebtCancellations: (state) => state.debtCancellations.length > 0,
    // The debt popup takes precedence, so the announcements modal waits until it is known whether there is one.
    isAnnouncementsModalBlocked: (state, getters, rootState, rootGetters) =>
      getters.hasDebtCancellations ||
      (rootGetters["auth/isCustomer"] && !state.debtCancellationsSettled),

    notifications: (state) => state.notifications,
    placementPreview: (state) => state.placementPreview,
    notificationPlacement: (state, getters, rootState, rootGetters) =>
      rootGetters["auth/user"]?.notificationsSettings.notificationsPlacement,
  },

  mutations: {
    setReadAt(state, readAt) {
      state.readAt = readAt;
    },

    setUnreadNotifications(state, unreadNotifications) {
      state.unreadNotifications = unreadNotifications;
    },

    setDebtCancellations(state, debtCancellations) {
      state.debtCancellations = debtCancellations;
    },

    setDebtCancellationsSettled(state, debtCancellationsSettled) {
      state.debtCancellationsSettled = debtCancellationsSettled;
    },

    removeDebtCancellations(state, notificationsIds) {
      state.debtCancellations = state.debtCancellations.filter(
        (debtCancellation) => !notificationsIds.includes(debtCancellation.id),
      );
    },

    setHasUnreadMessages(state, hasUnreadMessages) {
      state.hasUnreadMessages = hasUnreadMessages;
    },

    showNotification(state, notification /** Notification | null */) {
      if (notification && notification.id === undefined) {
        notification.id = ++notificationSequence;
      }

      state.notifications.push(notification);
    },

    setPlacementPreview(state, placementPreview) {
      state.placementPreview = placementPreview;
    },

    clearNotification(state, notification) {
      if (!notification) {
        state.notifications = [];

        return;
      }

      const index = state.notifications.indexOf(notification);

      if (index !== -1) {
        state.notifications.splice(index, 1);
      }
    },

    resetState(state) {
      resetStateToDefault(state, defaultState);
    },
  },

  actions: {
    async getUnread({ commit, rootState }, { perPage }) {
      let q = {};

      if (perPage) {
        q.perPage = perPage;
      }

      const res = await axios.get(`${API_URL}/notifications/unread`, {
        params: q,
      });

      if (!res.data || res.data.status !== true) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("setReadAt", res.data.readAt);
      commit("setUnreadNotifications", res.data.unreadNotifications ?? {});
      commit("setHasUnreadMessages", res.data.hasUnreadMessages);

      const currentUnseenAnnouncementsIds = rootState.announcements.unseenAnnouncementsIds;
      const unseenAnnouncementsIds = res.data.unseenAnnouncementsIds ?? [];

      if (
        unseenAnnouncementsIds.length !== currentUnseenAnnouncementsIds.length ||
        unseenAnnouncementsIds.some((id, index) => id !== currentUnseenAnnouncementsIds[index])
      ) {
        commit("announcements/setAnnouncementsThatUserShouldSeeAtLogin", unseenAnnouncementsIds, {
          root: true,
        });
      }

      return res.data;
    },

    async read(_, notificationData) {
      const res = await axios.post(`${API_URL}/notifications/read`, notificationData);

      if (res.status !== 200 || !res.data || res.data.status !== true) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getDebtCancellations({ commit }, { isLastAttempt }) {
      try {
        const res = await axios.get(`${API_URL}/notifications/debtCancellations`);

        if (!res.data || res.data.status !== true) {
          throw new Error("Error occurred while processing your request.");
        }

        commit("setDebtCancellations", res.data.debtCancellations ?? []);
        commit("setDebtCancellationsSettled", true);
      } catch (error) {
        // A request aborted by logout must not leave the flag set for the next session
        if (isLastAttempt && !axios.isCancel(error)) {
          commit("setDebtCancellationsSettled", true);
        }

        throw error;
      }
    },

    async acknowledgeDebtCancellations({ commit, dispatch, state }) {
      const notificationsIds = state.debtCancellations.map(
        (debtCancellation) => debtCancellation.id,
      );

      const res = await axios.post(`${API_URL}/notifications/readByIds`, { notificationsIds });

      if (res.status !== 200 || !res.data || res.data.status !== true) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("removeDebtCancellations", notificationsIds);

      dispatch("getUnread", { perPage: 5 }).catch(console.error);
    },

    async updateSubjectNotificationsSettings(
      _,
      { subjectId, subjectType, subjectNotificationsSettingsData },
    ) {
      const res = await axios.put(
        `${API_URL}/notifications/${subjectType}/${subjectId}/settings`,
        subjectNotificationsSettingsData,
      );

      if (res.status !== 200 || !res.data || res.data.status !== true) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },
  },
};

export default store;
